import React, { useContext, useMemo, useState } from "react";
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { MDBBtn, MDBIcon } from 'mdb-react-ui-kit';
import Backlog from "./Backlog";
import GlyphView from "./GlyphView";
import { Glyph, GlyphLayer, TaskType } from "konect-model";
import { IdeaBoxEntry } from "konect-model";
import { Project } from "konect-model";
import UserControlService from "../../services/user-control-service";
import { nanoid } from "nanoid";
import "./GlyphGrouping.css"
import { AppContext } from "../App";

interface GlyphGroupingProps {
    project: Project;
    glyphs: Glyph[];
    backlog: IdeaBoxEntry[];
    onRemove: (id: string) => void;
    onDraw: (id: string) => void;
    onCreate: (name: string, description: string) => void;
    setGlyphs: (glyphs: Glyph[]) => void;
    setBacklog: (entries: IdeaBoxEntry[]) => void;
    generateGlyphs: Function;
}

export enum DropType {
    LAYER = "LAYER",
    BACKLOG = "BACKLOG",
    GLYPH = "GLYPH",
    TASK = "TASK"
}

export default function GlyphGroupingCards(props: GlyphGroupingProps) {
    const { project } = useContext(AppContext);
    const [isDragging, setIsDragging] = useState<any>(false);

    const onDragEnd = (result: any) => {

        setIsDragging(false);

        const { destination, source } = result;
        if (!destination || (destination.droppableId.includes("backlog") && source.droppableId.includes("backlog"))) {
            return;
        }

        UserControlService.update();

        let destinationEntries: any = getEntriesOf(destination);
        let sourceEntries: any = getEntriesOf(source);

        //swap dragged elements within their arrays
        if (destination.droppableId !== source.droppableId) {
            destinationEntries.splice(destination.index, 0, sourceEntries[source.index]);
            sourceEntries.splice(source.index, 1);
        } else if (destination.index !== source.index) {
            const swapEntry = sourceEntries.splice(source.index, 1)[0];
            sourceEntries.splice(destination.index, 0, swapEntry);
        }

        //remove empty glyph layers except the last one
        for (const glyph of props.glyphs) {
            glyph.layers = glyph.layers.filter((layer: GlyphLayer, index) => index >= glyph.layers.length - 1 || layer.entries.length > 0);
            if (glyph.layers[glyph.layers.length - 1].entries.length > 0) {
                glyph.layers.push(new GlyphLayer(nanoid()));
            }
        }

        props.project.glyphs = props.glyphs;
        props.setGlyphs([...props.glyphs]);
        props.setBacklog([...props.backlog]);
    };

    const getEntriesOf = (target: any) => {
        const isBacklog = target.droppableId.includes("backlog");
        let entries: any = [];
        if (target.droppableId === "glyph-rows") {
            entries = props.glyphs;
        } else if (target.droppableId.includes("glyph-layer")) {
            for (const glyph of props.glyphs) {
                const layer = glyph.layers.find((layer: GlyphLayer) => "glyph-layer" + layer.id === target.droppableId);
                if (layer) {
                    entries = layer.entries;
                    break;
                }
            }
        } else if (isBacklog) {
            entries = props.backlog;
        } else {
            //@ts-ignore
            entries = props.glyphs.find((el: Glyph) => target.droppableId === el.id).layers;
        }

        return entries;
    }

    const onDragStart = (e: any) => {
        setIsDragging(e);
    }

    const renderBacklogs = useMemo(() => {
        return <div className="backlog-container"><Backlog
            entries={props.backlog}
            type={TaskType.INFORMATION}
            name={"Perception Backlog"}/>

            <Backlog
                entries={props.backlog}
                type={TaskType.INTERACTION}
                name={"Interaction Backlog"}
            /></div>
    }, [props.backlog]);

    const renderGlyphs = useMemo(() => {
        if (props.glyphs.length < 1) {
            //@ts-ignore
            return <div className={"help-info"}>
                Create a Glyph to get started<br />
                {(project.taskModel && project.taskModel.resourceModel && project.taskModel.resourceModel.typeDefinition) &&
                    <span onClick={() => { props.generateGlyphs() }} className="auto-create">AUTO CREATE</span>}
            </div>
        }
        return props.glyphs.map((glyph: Glyph, index: number) => {
            return <GlyphView
                key={glyph.id}
                glyph={glyph}
                index={index}
                name={glyph.name}
                onRemove={props.onRemove}
                onDraw={props.onDraw}
                isDragging={isDragging}
            />
        })
    }, [props.glyphs, isDragging]);  // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
                <Droppable droppableId="glyph-rows" direction="vertical" type={DropType.GLYPH}>
                    {provided => (
                        <div {...provided.droppableProps} ref={provided.innerRef} style={{ flex: "1", marginRight: "0.5rem", width: "0px", display: "flex", flexDirection: "column" }}>
                            <div style={{ flex: "1", textAlign: "left", marginBottom: "20px", minHeight: "0", position: "relative" }} >
                                <div style={{height: "100%", overflow: "auto", position: "absolute", borderRadius: "10px", width: "100%"}} className="p-3 border bg-white overflow-none px-2 ">
                                {renderGlyphs}
                                {provided.placeholder}
                                </div>
                            </div>
                            <div className="text-center">
                                <MDBBtn floating className='mx-1' onClick={() => { props.onCreate("", "") }} >
                                    <MDBIcon fas icon='plus' />
                                </MDBBtn>
                            </div>
                        </div>
                    )}
                </Droppable>
                {renderBacklogs}
        </DragDropContext>
    )
}